// ConfirmationV2 — piloto visual da tela "Agendamento confirmado".
// Componente puramente apresentacional: recebe todos os dados já calculados
// pela BookingPage (bookingResult, serviços, extras, empresa) e NÃO cria,
// altera ou cancela nenhum agendamento, pagamento ou envio de WhatsApp — só
// re-renderiza o que já existe hoje em um layout mais moderno e compacto,
// seguindo o mesmo design system de pilot/MyAccountV2.tsx.
//
// Gated por company.settings.confirmation_ui_v2_pilot === true — ausência
// ou qualquer outro valor mantém a tela antiga (fallback seguro).
//
// Rodada 2 (refinamento visual, 09/08/2026):
// - Layout sem flex:1 forçado — altura segue o conteúdo, cresce até o teto
//   (100dvh mobile / 90dvh desktop) e só então passa a rolar. Sem
//   margin-top:auto nem justify-content:space-between empurrando ações.
// - Endereço opcional (settings.show_confirmation_address), com link para
//   Google Maps e botão de copiar.
// - "Ver meus agendamentos" só aparece com sessão comprovada pelo próprio
//   bookingResult (nunca por leitura direta do localStorage).
// - Protótipo visual inerte de upsell — só em modo prévia do dono, sem
//   endpoint, sem alterar total, sem nenhuma ação real ao clicar.
import React, { useState } from 'react';
import { Box, Typography, Dialog, DialogContent, IconButton, Divider } from '@mui/material';
import { Close, CheckCircle, Place, ContentCopy, Check } from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Texto legível (branco/preto) sobre uma cor sólida — luminância relativa.
// Mesma função usada em pilot/MyAccountV2.tsx — mantém "contraste automático"
// consistente em todo o painel do cliente.
function onColor(hex?: string): string {
  if (!hex || hex[0] !== '#') return '#fff';
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return '#fff';
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.6 ? '#111111' : '#ffffff';
}

// Tokens de tema — espelham pilot/MyAccountV2.tsx (mesma família visual da
// área "Meus Agendamentos" pedida como referência).
function palette(isDark: boolean) {
  return isDark
    ? { bg: '#0f0f0f', surface: '#1a1a1a', surfaceAlt: '#222222', border: '#2e2e2e',
        tPrimary: '#f1f5f9', tSecondary: '#94a3b8', tMuted: '#64748b', divider: '#242424' }
    : { bg: '#f7f8fa', surface: '#ffffff', surfaceAlt: '#f5f5f5', border: '#ececec',
        tPrimary: '#111111', tSecondary: '#666666', tMuted: '#999999', divider: '#f4f4f4' };
}

// Dados 100% sintéticos — o protótipo é só visual, nunca representa um
// produto real nem um preço real. Ver PROTOTYPE_NOTE no rodapé do card.
const UPSELL_PREVIEW_ITEM = {
  name: 'Pomada modeladora premium',
  imageEmoji: '🧴',
  originalPrice: 45,
  offerPrice: 29,
};

export interface ConfirmationV2Props {
  open: boolean;
  onClose: () => void;
  isDark: boolean;
  accentColor: string;
  company: any;
  slug: string;
  selDate: Date | null;
  selTime: string;
  selPro?: { name?: string } | null;
  selected: Array<{ name: string; price?: number }>;
  selectedBumps: Array<{ name: string; price?: number; description?: string }>;
  payMethodSel?: string;
  showAddToCalendar: boolean;
  onAddToCalendar: () => void;
  /** settings.show_confirmation_address === true — default false, opt-in por empresa */
  showAddress: boolean;
  /** deriva do próprio bookingResult (customer_token/customer retornados pelo backend
   *  desta criação), nunca do localStorage cru — garante que a sessão é desta empresa */
  hasValidSession: boolean;
  /** modo prévia do dono (?preview=true) — único contexto em que o protótipo
   *  visual (sintético) de upsell pode aparecer quando não há oferta real */
  isPreview: boolean;
  /** oferta real vinda do backend (GET .../upsell) — null se não há upsell
   *  configurado para o serviço, já aceito, já no order bump, etc. */
  upsellOffer?: { service_upsell_id: number; name: string; description: string | null; image_url: string | null; normal_price: number; offer_price: number | null; savings: number } | null;
  /** upsell já aceito para este booking (snapshot devolvido pelo backend) */
  upsellAccepted?: { item_name: string; item_price: number } | null;
  upsellAccepting?: boolean;
  onAcceptUpsell?: () => void;
}

export function ConfirmationV2({
  open, onClose, isDark, accentColor, company, slug,
  selDate, selTime, selPro, selected, selectedBumps,
  payMethodSel, showAddToCalendar, onAddToCalendar,
  showAddress, hasValidSession, isPreview,
  upsellOffer, upsellAccepted, upsellAccepting, onAcceptUpsell,
}: ConfirmationV2Props) {
  const C = palette(isDark);
  const onAccent = onColor(accentColor);
  const [copied, setCopied] = useState(false);
  const [upsellHint, setUpsellHint] = useState(false);
  const [realUpsellDismissed, setRealUpsellDismissed] = useState(false);

  const serviceTotal = (selected || []).reduce((acc, s) => acc + Number(s.price || 0), 0);
  const bumpTotal = (selectedBumps || []).reduce((acc, b) => acc + Number(b.price || 0), 0);
  const acceptedUpsellPrice = upsellAccepted ? Number(upsellAccepted.item_price) : 0;
  const finalTotal = serviceTotal + bumpTotal + acceptedUpsellPrice;
  const serviceNames = (selected || []).map((s) => s.name).join(' + ') || 'Serviço';
  const isCashSelected = !!(company?.settings?.mp_cash_enabled && payMethodSel === 'cash');
  const address = (company?.address || '').trim();
  const showAddressLine = showAddress && !!address;
  const showMyBookingsButton = hasValidSession;
  // Oferta real tem prioridade; o protótipo sintético só aparece em modo
  // prévia quando NÃO existe oferta real configurada para o serviço — assim
  // o dono continua podendo visualizar o formato mesmo antes de cadastrar
  // um upsell de verdade, sem nunca misturar dado real com sintético.
  const showRealUpsell = !!(upsellOffer && !upsellAccepted && !realUpsellDismissed);
  const showUpsellPreview = isPreview && !upsellOffer && !upsellAccepted;
  const upsellSavings = UPSELL_PREVIEW_ITEM.originalPrice - UPSELL_PREVIEW_ITEM.offerPrice;

  // "Ver meus agendamentos" é a ação principal quando disponível (sessão
  // válida desta empresa); senão "Adicionar ao calendário" assume o
  // destaque — nunca os dois nem nenhum sem destaque.
  const primaryIsMyBookings = showMyBookingsButton;

  const goToMyBookings = () => {
    window.location.href = window.location.origin + '/meus-agendamentos?s=' + encodeURIComponent(slug);
  };

  const copyAddress = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!address) return;
    navigator.clipboard?.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const openMaps = () => {
    if (!address) return;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank', 'noopener,noreferrer');
  };

  const primaryBtnStyle: React.CSSProperties = {
    width: '100%', minHeight: 46, borderRadius: 14, border: 'none', cursor: 'pointer',
    background: accentColor, color: onAccent, fontWeight: 800, fontSize: 14.5,
  };
  const secondaryBtnStyle: React.CSSProperties = {
    width: '100%', minHeight: 46, borderRadius: 14, cursor: 'pointer',
    background: 'transparent', color: C.tPrimary, fontWeight: 800, fontSize: 14.5,
    border: `1px solid ${C.border}`,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{ paper: { sx: {
        width: { xs: '100%', sm: 560 },
        maxWidth: { xs: '100%', sm: 560 },
        m: { xs: 0, sm: 2 },
        borderRadius: { xs: 0, sm: 4 },
        maxHeight: { xs: '100dvh', sm: '90dvh' },
        bgcolor: C.bg,
        backgroundImage: 'none',
        display: 'flex',
        flexDirection: 'column',
      } } }}
    >
      {/* Único container com scroll — cabeçalho, resumo e ações fluem juntos.
          Sem flex:1 em nenhum filho: a altura segue o conteúdo até o teto
          acima; só rola quando o conteúdo realmente não cabe. */}
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <Box sx={{
          pt: 'max(20px, calc(env(safe-area-inset-top, 0px) + 12px))', pb: 2.5, px: { xs: 2.5, sm: 3 },
          display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1,
          position: 'relative', flexShrink: 0,
        }}>
          <IconButton
            aria-label="Fechar"
            title="Fechar"
            onClick={onClose}
            sx={{
              position: 'absolute', top: 'max(10px, env(safe-area-inset-top, 0px))', right: 10,
              width: 48, height: 48,
              color: C.tSecondary, bgcolor: C.surfaceAlt,
              '&:hover': { bgcolor: C.border },
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>

          <Box sx={{
            width: 56, height: 56, borderRadius: '50%', bgcolor: '#dcfce7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5,
          }}>
            <CheckCircle sx={{ fontSize: 32, color: '#16a34a' }} />
          </Box>

          <Typography sx={{ fontWeight: 800, fontSize: 18.5, color: C.tPrimary, lineHeight: 1.2 }}>
            Agendamento confirmado
          </Typography>
          <Typography sx={{ fontSize: 13, color: C.tSecondary, maxWidth: 300, lineHeight: 1.5 }}>
            Seu horário foi reservado com sucesso.
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3 }, pb: 1.5, flexShrink: 0, minWidth: 0 }}>
          <Box sx={{
            bgcolor: C.surface, borderRadius: 3, border: `1px solid ${C.border}`,
            p: { xs: 2, sm: 2.4 }, boxShadow: isDark ? 'none' : '0 1px 8px rgba(0,0,0,0.05)',
          }}>
            <Typography sx={{
              fontWeight: 800, fontSize: 16.5, color: C.tPrimary, mb: 1.2,
              wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.3,
            }}>
              {serviceNames}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 1.4 }}>
              <Chip label={selDate ? format(selDate, "EEE, d 'de' MMMM", { locale: ptBR }) : ''} accent isDark={isDark} />
              <Chip label={selTime} C={C} />
              {selPro?.name && <Chip label={selPro.name} C={C} />}
            </Box>

            {showAddressLine && (
              <Box sx={{
                display: 'flex', alignItems: 'stretch', gap: 0.5, mb: 1.6,
                borderRadius: 2, border: `1px solid ${C.border}`, overflow: 'hidden',
              }}>
                <Box
                  role="link"
                  onClick={openMaps}
                  sx={{
                    flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 0.8,
                    px: 1.2, py: 1, cursor: 'pointer', '&:hover': { bgcolor: C.surfaceAlt },
                  }}
                >
                  <Place sx={{ fontSize: 16, color: C.tMuted, mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, color: C.tSecondary, lineHeight: 1.4, wordBreak: 'break-word' }}>
                    {address}
                  </Typography>
                </Box>
                <IconButton
                  aria-label="Copiar endereço"
                  title="Copiar endereço"
                  onClick={copyAddress}
                  sx={{ width: 44, height: 44, alignSelf: 'center', mr: 0.4, flexShrink: 0, color: copied ? '#16a34a' : C.tMuted }}
                >
                  {copied ? <Check sx={{ fontSize: 18 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
            )}
            {showAddressLine && copied && (
              <Typography sx={{ fontSize: 11.5, color: '#16a34a', fontWeight: 700, mt: -1, mb: 1.4 }}>
                Endereço copiado
              </Typography>
            )}

            {selectedBumps.length > 0 && (
              <Box sx={{ mb: 1.6, p: 1.4, bgcolor: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 2.2 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: C.tMuted, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.9 }}>
                  Extras adicionados
                </Typography>
                {selectedBumps.map((b, i) => (
                  <Box key={`cv2-bump-${i}`} sx={{
                    display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.6,
                    borderBottom: i < selectedBumps.length - 1 ? `1px solid ${C.divider}` : 'none',
                  }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.tPrimary, wordBreak: 'break-word', minWidth: 0, flex: 1 }}>
                      + {b.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: C.tPrimary, flexShrink: 0 }}>
                      R$ {Number(b.price || 0).toFixed(2).replace('.', ',')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Divider sx={{ borderColor: C.divider, mb: 1.2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: C.tPrimary }}>Total</Typography>
              <Typography sx={{ fontSize: 19, fontWeight: 900, color: C.tPrimary }}>
                R$ {finalTotal.toFixed(2).replace('.', ',')}
              </Typography>
            </Box>
            {isCashSelected && (
              <Typography sx={{ fontSize: 11.5, color: C.tMuted, mt: 0.6 }}>
                Pagamento no estabelecimento
              </Typography>
            )}
          </Box>

          {/* ── Upsell já aceito — resumo simples, some da lista de extras
              pendentes; o valor já está somado no Total acima. ── */}
          {upsellAccepted && (
            <Box sx={{ mt: 1.6, p: 1.4, bgcolor: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 2.2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.tPrimary, wordBreak: 'break-word', minWidth: 0, flex: 1 }}>
                ✓ {upsellAccepted.item_name} adicionado ao seu atendimento
              </Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: C.tPrimary, flexShrink: 0 }}>
                R$ {acceptedUpsellPrice.toFixed(2).replace('.', ',')}
              </Typography>
            </Box>
          )}

          {/* ── Upsell real — vindo do backend, um único item, nunca dois.
              "Adicionar" chama onAcceptUpsell (POST idempotente); o total
              acima só muda depois que o backend confirma o aceite. ── */}
          {showRealUpsell && upsellOffer && (
            <Box sx={{
              mt: 1.6, borderRadius: 3, border: `1px solid ${C.border}`,
              bgcolor: C.surfaceAlt, p: 1.8,
            }}>
              <Typography sx={{ fontWeight: 800, fontSize: 14.5, color: C.tPrimary, mb: 1.2 }}>
                Aproveite também
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
                {upsellOffer.image_url ? (
                  <Box component="img" src={upsellOffer.image_url} alt="" sx={{
                    width: 52, height: 52, borderRadius: 2, objectFit: 'cover',
                    border: `1px solid ${C.border}`, flexShrink: 0,
                  }} />
                ) : (
                  <Box sx={{
                    width: 52, height: 52, borderRadius: 2, bgcolor: C.surface, border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.tPrimary, wordBreak: 'break-word' }}>
                    {upsellOffer.name}
                  </Typography>
                  {upsellOffer.description && (
                    <Typography sx={{ fontSize: 11, color: C.tSecondary, mt: 0.2, wordBreak: 'break-word' }}>
                      {upsellOffer.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.7, mt: 0.3, flexWrap: 'wrap' }}>
                    {upsellOffer.offer_price != null && upsellOffer.offer_price < upsellOffer.normal_price && (
                      <Typography sx={{ fontSize: 11.5, color: C.tMuted, textDecoration: 'line-through' }}>
                        R$ {upsellOffer.normal_price.toFixed(2).replace('.', ',')}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: accentColor }}>
                      R$ {(upsellOffer.offer_price ?? upsellOffer.normal_price).toFixed(2).replace('.', ',')}
                    </Typography>
                    {upsellOffer.savings > 0 && (
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#16a34a' }}>
                        economize R$ {upsellOffer.savings.toFixed(2).replace('.', ',')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.8, mt: 1.4 }}>
                <button
                  disabled={upsellAccepting}
                  onClick={onAcceptUpsell}
                  style={{
                    flex: 1, minHeight: 40, borderRadius: 12, border: 'none', cursor: upsellAccepting ? 'default' : 'pointer',
                    background: accentColor, color: onAccent, fontWeight: 700, fontSize: 13, opacity: upsellAccepting ? 0.7 : 1,
                  }}
                >
                  {upsellAccepting ? 'Adicionando...' : 'Adicionar ao meu atendimento'}
                </button>
                <button
                  disabled={upsellAccepting}
                  onClick={() => setRealUpsellDismissed(true)}
                  style={{
                    flex: 1, minHeight: 40, borderRadius: 12, cursor: 'pointer',
                    background: 'transparent', color: C.tSecondary, fontWeight: 700, fontSize: 13,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  Agora não
                </button>
              </Box>
            </Box>
          )}

          {/* ── Protótipo visual de upsell — SOMENTE em modo prévia do dono,
              e só quando não há oferta real configurada para o serviço.
              Dados sintéticos, sem endpoint, sem alterar total, sem ação real. ── */}
          {showUpsellPreview && (
            <Box sx={{
              mt: 1.6, borderRadius: 3, border: `1px dashed ${C.border}`,
              bgcolor: C.surfaceAlt, p: 1.8, position: 'relative',
            }}>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: C.tMuted, textTransform: 'uppercase', letterSpacing: 0.7, mb: 1 }}>
                Prévia visual · não é uma função real ainda
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 14.5, color: C.tPrimary, mb: 1.2 }}>
                Aproveite também
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
                <Box sx={{
                  width: 52, height: 52, borderRadius: 2, bgcolor: C.surface, border: `1px solid ${C.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0,
                }}>
                  {UPSELL_PREVIEW_ITEM.imageEmoji}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.tPrimary, wordBreak: 'break-word' }}>
                    {UPSELL_PREVIEW_ITEM.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.7, mt: 0.3 }}>
                    <Typography sx={{ fontSize: 11.5, color: C.tMuted, textDecoration: 'line-through' }}>
                      R$ {UPSELL_PREVIEW_ITEM.originalPrice.toFixed(2).replace('.', ',')}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: accentColor }}>
                      R$ {UPSELL_PREVIEW_ITEM.offerPrice.toFixed(2).replace('.', ',')}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#16a34a' }}>
                      economize R$ {upsellSavings.toFixed(2).replace('.', ',')}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.8, mt: 1.4 }}>
                <button
                  onClick={() => { setUpsellHint(true); setTimeout(() => setUpsellHint(false), 1800); }}
                  style={{
                    flex: 1, minHeight: 40, borderRadius: 12, border: 'none', cursor: 'pointer',
                    background: accentColor, color: onAccent, fontWeight: 700, fontSize: 13,
                  }}
                >
                  Adicionar por R$ {UPSELL_PREVIEW_ITEM.offerPrice.toFixed(2).replace('.', ',')}
                </button>
                <button
                  onClick={() => { setUpsellHint(true); setTimeout(() => setUpsellHint(false), 1800); }}
                  style={{
                    flex: 1, minHeight: 40, borderRadius: 12, cursor: 'pointer',
                    background: 'transparent', color: C.tSecondary, fontWeight: 700, fontSize: 13,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  Agora não
                </button>
              </Box>
              {upsellHint && (
                <Typography sx={{ fontSize: 11, color: C.tMuted, mt: 1, textAlign: 'center' }}>
                  Prévia visual — nenhuma ação real foi executada
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Ações — todas com altura mínima de 44px (touch target). Sem
            margin-top:auto/space-between: seguem o fluxo normal do conteúdo. */}
        <Box sx={{
          px: { xs: 2.5, sm: 3 }, pb: 'max(18px, env(safe-area-inset-bottom, 0px))', pt: 0.5,
          display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0,
        }}>
          {showAddToCalendar && (
            <button onClick={onAddToCalendar} style={primaryIsMyBookings ? secondaryBtnStyle : primaryBtnStyle}>
              Adicionar ao calendário
            </button>
          )}

          {showMyBookingsButton && (
            <button onClick={goToMyBookings} style={primaryIsMyBookings ? primaryBtnStyle : secondaryBtnStyle}>
              Ver meus agendamentos
            </button>
          )}

          <button
            onClick={onClose}
            style={{
              width: '100%', minHeight: 44, borderRadius: 14, border: 'none', cursor: 'pointer',
              background: 'transparent', color: C.tMuted, fontWeight: 700, fontSize: 13.5,
            }}
          >
            Fechar
          </button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function Chip({ label, accent, isDark, C }: { label: string; accent?: boolean; isDark?: boolean; C?: any }) {
  if (!label) return null;
  if (accent) {
    return (
      <Box sx={{ px: 1.3, py: 0.6, bgcolor: isDark ? 'rgba(59,130,246,0.16)' : '#eff6ff', borderRadius: 2, border: isDark ? '1px solid rgba(59,130,246,0.3)' : '1px solid #dbeafe' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: isDark ? '#93c5fd' : '#1d4ed8', textTransform: 'capitalize' }}>{label}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ px: 1.3, py: 0.6, bgcolor: C.surfaceAlt, borderRadius: 2, border: `1px solid ${C.border}` }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.tPrimary }}>{label}</Typography>
    </Box>
  );
}
